import { FC, SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useFragment } from 'react-relay';
import { useField } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AutocompleteBase, {
  AutocompleteBaseProps,
  Option,
} from 'components/AutocompleteBase';

import { ServiceGroupSelect_data$key } from '__generated__/ServiceGroupSelect_data.graphql';

type Props = {
  data: ServiceGroupSelect_data$key;
} & Omit<AutocompleteBaseProps, 'options'>;

const ServiceGroupSelect: FC<Props> = ({ data, ...rest }) => {
  const [selected, setSelected] = useState<Option | null>(null);
  const [{ value }, , { setValue }] = useField('services');

  const { allServiceGroups } = useFragment(
    graphql`
      fragment ServiceGroupSelect_data on Query {
        allServiceGroups {
          id
          name

          allServices {
            id
            name
          }
        }
      }
    `,
    data
  );

  const handleChange = useCallback((event: SyntheticEvent, value) => {
    setSelected(value);
  }, []);

  const handleAssign = useCallback(() => {
    const group = allServiceGroups.find(({ id }) => id === selected?.id);
    if (!group) return;

    setValue([...value, ...group.allServices]);
  }, [allServiceGroups, selected?.id, setValue, value]);

  const options = useMemo(
    () => allServiceGroups.map(({ allServices, ...rest }) => rest),
    [allServiceGroups]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row' }}>
      <AutocompleteBase
        fullWidth
        options={options}
        value={selected}
        label="Paslaugų paketai"
        onChange={handleChange}
        {...rest}
      />

      <Box sx={{ width: '50px', pt: '10px', pl: '10px' }}>
        <IconButton onClick={handleAssign} disabled={!selected}>
          <Tooltip title="Priskirti paketo paslaugas">
            <AddIcon />
          </Tooltip>
        </IconButton>
      </Box>
    </Box>
  );
};

export default ServiceGroupSelect;
